import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ACTIVITY_TYPE_MAP,
  LEAD_RATING_MAP,
  LEAD_STATUS_MAP,
  TIMELINE_KIND_DEFS,
  attachmentKind,
  type TimelineEvent,
  type TimelineKind,
} from '@goyone/shared';
import { api } from '../lib/api';
import { dateTime, relative } from '../lib/format';
import { Icon, type IconName } from './Icon';
import { OwnerChip } from './People';
import { AudioPlayer } from './AttachmentPreview';
import { Badge, ErrorState, Modal } from './ui';
import { iconForKind } from './AttachmentPreview';
import {
  RecordingPlayerModal,
  recordingKind,
  recordingLabel,
} from './MeetingRecording';

/**
 * تاريخ السجل في موضع واحد.
 *
 * كان مفرَّقًا على ثلاثة: الأنشطة في بطاقتها، والمرفقات في بطاقتها، وتغيّرات
 * الحقول في سجل التدقيق الذي لا يراه إلا مدير النظام. فمن أراد أن يعرف
 * «متى انتقل هذا العميل إليّ وماذا جرى قبلها» يفتح ثلاث شاشات ويرتّبها
 * بذهنه — إن كانت له صلاحية أصلًا.
 *
 * والقيم تصل خامًا وتُترجم هنا: الواجهة تملك دليل الفريق الذي يحوّل معرّف
 * المستخدم إلى اسم وصورة، والخادم لا يملكه.
 */

const KIND_ICONS: Record<TimelineKind, IconName> = {
  created: 'plus',
  activity: 'clipboard',
  field: 'refresh',
  attachment: 'download',
};

export function RecordTimeline({
  entity,
  id,
  title = 'الخطّ الزمني',
  compact = false,
}: {
  entity: 'lead' | 'account' | 'contact';
  id: string;
  title?: string;
  /**
   * عرضٌ مختصر: ارتفاعُ نحو خمسة أحداث والباقي يُمرَّر داخل البطاقة —
   * فالملفّ لا يطول بطول تاريخه. وزرُّ التكبير يفتح السجل كله في نافذة.
   */
  compact?: boolean;
}) {
  const [filter, setFilter] = useState<TimelineKind | undefined>();
  const [expanded, setExpanded] = useState(false);

  const q = useQuery({
    queryKey: ['timeline', entity, id],
    queryFn: () => api<TimelineEvent[]>(`/crm/timeline/${entity}/${id}`),
    enabled: Boolean(id),
  });

  const all = q.data ?? [];
  const shown = filter ? all.filter((e) => e.kind === filter) : all;

  /* لا يُعرض مرشّح لنوع لا حدث له: زرٌّ يُفرغ القائمة دائمًا ليس مرشّحًا. */
  const present = new Set(all.map((e) => e.kind));

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">{title}</h2>
        {all.length > 0 && (
          <div className="tl-filters">
            <button
              type="button"
              className={`chip${!filter ? ' chip-on' : ''}`}
              onClick={() => setFilter(undefined)}
            >
              الكل
            </button>
            {TIMELINE_KIND_DEFS.filter((d) => present.has(d.key)).map((d) => (
              <button
                key={d.key}
                type="button"
                className={`chip${filter === d.key ? ' chip-on' : ''}`}
                onClick={() => setFilter(filter === d.key ? undefined : d.key)}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        {compact && all.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-ghost"
            onClick={() => setExpanded(true)}
            title="اعرض السجل كله"
            aria-label="اعرض السجل كله"
          >
            <Icon name="chevronUp" size={14} />
          </button>
        )}
      </div>

      <div className="card-body">
        {q.isPending && <div className="skeleton" style={{ height: 140 }} />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}

        {q.data && shown.length === 0 && (
          <p className="mute small center">
            {filter ? 'لا أحداث من هذا النوع.' : 'لا أحداث بعد.'}
          </p>
        )}

        <div className={`timeline${compact ? ' tl-compact' : ''}`}>
          {/*
            فهرس الظهور يُسقَف: تأخيرٌ يتصاعد بلا سقفٍ كان يُبقي ما بعد
            الأحداث الأولى شفافًا حتى ينقضي تأخيرُه — فيبدو الخطُّ بين
            الأحداث مقطوعًا عند التمرير.
          */}
          {shown.map((e, i) => (
            <TimelineRow key={e.id} event={e} index={Math.min(i, 6)} />
          ))}
        </div>
      </div>

      {/* السجل كله — النافذة تعيد المكوّن نفسه بلا اختصار. */}
      {expanded && (
        <Modal title={title} onClose={() => setExpanded(false)} wide>
          <RecordTimeline entity={entity} id={id} title={title} />
        </Modal>
      )}
    </div>
  );
}

function TimelineRow({ event, index }: { event: TimelineEvent; index: number }) {
  /* مشغّل تسجيل الاجتماع — لكل صفٍّ حالته، والغالب لا تسجيل له. */
  const [playingRec, setPlayingRec] = useState(false);

  return (
    <div className="tl-item tl-item-icon reveal" style={{ ['--reveal-i' as string]: index }}>
      <span className="tl-dot" data-kind={event.kind}>
        <Icon
          name={
            event.kind === 'attachment'
              ? iconForKind(attachmentKind(event.subtype ?? ''))
              : KIND_ICONS[event.kind]
          }
          size={13}
        />
      </span>

      <div className="tl-body">
        <div className="tl-line">
          <EventText event={event} />
        </div>

        <div className="tiny mute tl-meta">
          {event.actorName ?? (event.actorId ? '' : 'النظام')}
          {event.actorId && !event.actorName && <OwnerChip ownerId={event.actorId} size={18} />}
          <span title={dateTime(event.at)}>{relative(event.at)}</span>
        </div>

        {/* نتيجة المكالمة وملاحظتها وتسجيلها — الملف يحكي المكالمة كاملة. */}
        {event.outcome && (
          <div className="tl-detail">
            <Badge kind="info">{event.outcome}</Badge>
          </div>
        )}

        {event.detail && <div className="tiny mute tl-detail">{event.detail}</div>}

        {event.recordingId && (
          <div className="tl-detail">
            <AudioPlayer id={event.recordingId} />
          </div>
        )}

        {/* تسجيل الاجتماع كتسجيل المكالمة سواء — يُشغَّل من الخطّ نفسه. */}
        {event.recordingUrl && (
          <div className="tl-detail">
            {recordingKind(event.recordingUrl)?.kind === 'external' ? (
              <a
                className="btn btn-sm"
                href={event.recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="المنصّة لا تسمح بالتشغيل داخل النظام — يُفتح عندها"
              >
                <Icon name="video" size={13} />
                تسجيل الاجتماع — على المنصّة
              </a>
            ) : (
              <button type="button" className="btn btn-sm" onClick={() => setPlayingRec(true)}>
                <Icon name="video" size={13} />
                {`شغّل ${recordingLabel(event.recordingUrl)}`}
              </button>
            )}
            {playingRec && (
              <RecordingPlayerModal
                url={event.recordingUrl}
                onClose={() => setPlayingRec(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * صياغة الحدث.
 *
 * الحقول لها صياغتان لا واحدة: «أُسند إلى فلان» حين لا سابق، و«نُقل من فلان
 * إلى فلان» حين له سابق. الصياغة الواحدة تُنتج «نُقل من لا أحد إلى فلان»،
 * وهي جملة لا يقولها أحد.
 */
function EventText({ event }: { event: TimelineEvent }) {
  if (event.kind === 'activity') {
    const type = ACTIVITY_TYPE_MAP[event.subtype as keyof typeof ACTIVITY_TYPE_MAP];
    const done = event.to === 'DONE';
    return (
      <>
        <span className="mute">{done ? `${type?.label ?? 'نشاط'} تمّ:` : `${type?.label ?? 'نشاط'} مجدول:`}</span>{' '}
        <strong>{event.title}</strong>
      </>
    );
  }

  if (event.kind === 'attachment') {
    return (
      <>
        <span className="mute">أُرفق:</span> <strong>{event.title}</strong>
      </>
    );
  }

  if (event.kind === 'created') {
    return <strong>{event.title}</strong>;
  }

  /* تغيّر حقل. */
  if (event.subtype === 'converted') {
    return <strong>{event.title}</strong>;
  }

  if (event.subtype === 'ownerId') {
    if (!event.to) {
      return (
        <>
          <span className="mute">أُلغي إسناد المسؤول</span>
          {event.from && (
            <>
              {' '}
              <span className="mute">— كان</span> <OwnerChip ownerId={event.from} size={18} />
            </>
          )}
        </>
      );
    }
    return event.from ? (
      <>
        <span className="mute">نُقل من</span> <OwnerChip ownerId={event.from} size={18} />{' '}
        <span className="mute">إلى</span> <OwnerChip ownerId={event.to} size={18} />
      </>
    ) : (
      <>
        <span className="mute">أُسند إلى</span> <OwnerChip ownerId={event.to} size={18} />
      </>
    );
  }

  /*
   * الترجمة تتراجع إلى القيمة الخام عند الغياب.
   *
   * الكتالوجات تخصّ العميل المحتمل، والخطّ الزمني نفسه يخدم العميل وجهة
   * الاتصال كذلك. وقيمة قديمة حُذفت من الكتالوج تبقى في السجل — عرضها
   * كما هي أصدق من عرض فراغ.
   */
  const labels: Record<string, Record<string, { label: string }>> = {
    status: LEAD_STATUS_MAP,
    rating: LEAD_RATING_MAP,
  };

  const label = (raw: string | null): string => {
    if (!raw) return '—';
    const map = event.subtype ? labels[event.subtype] : undefined;
    return map?.[raw]?.label ?? raw;
  };

  return (
    <>
      <span className="mute">{event.title}:</span>{' '}
      {event.from && (
        <>
          <span className="tl-old">{label(event.from)}</span>{' '}
          <span className="mute">←</span>{' '}
        </>
      )}
      <strong>{label(event.to)}</strong>
    </>
  );
}
